import { forwardRef } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  fadeInUpLarge, 
  fadeInUp, 
  viewportSettings, 
  defaultTransition, 
  withDelay 
} from "@/lib/animations";

interface CTASectionProps {
  onViewAllPacksClick: () => void;
}

export const CTASection = forwardRef<HTMLElement, CTASectionProps>(
  ({ onViewAllPacksClick }, ref) => {
    const { t } = useLanguage();

    return (
      <section ref={ref} className="py-32 md:py-48 relative overflow-hidden">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <motion.h2
              {...fadeInUpLarge}
              whileInView={fadeInUpLarge.animate}
              viewport={viewportSettings.default}
              transition={defaultTransition}
              className="text-4xl md:text-6xl lg:text-7xl font-display font-bold tracking-tighter mb-8"
            >
              {t(
                "Ready to Create?",
                "¿Listo para Crear?"
              )}
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={viewportSettings.default}
              transition={withDelay(0.1, defaultTransition)}
              className="text-lg md:text-xl text-muted-foreground mb-12 max-w-2xl mx-auto"
            >
              {t(
                "Start generating professional fashion imagery today with our curated AI prompt collections.",
                "Comienza a generar imágenes de moda profesionales hoy con nuestras colecciones de prompts de IA curadas."
              )}
            </motion.p>
            
            <motion.div
              {...fadeInUp}
              whileInView={fadeInUp.animate}
              viewport={viewportSettings.default}
              transition={withDelay(0.2, defaultTransition)}
            >
              <Button
                size="lg"
                onClick={onViewAllPacksClick}
                className="group text-base font-display px-8"
              >
                {t("View All Collections", "Ver Todas las Colecciones")}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </motion.div>
          </div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-accent/5 rounded-full blur-3xl" />
        </div>
      </section>
    );
  }
);

CTASection.displayName = "CTASection";

export default CTASection;